/**
 * Derive a breadcrumb path from a pathname.
 *
 * The default implementation:
 *   /app/dashboard         → ['Dashboard']
 *   /app/workspace/123     → ['Workspace', '123']
 *   /app/workspace/123/grid → ['Workspace', '123', 'Grid']
 *
 * Override by supplying a custom resolver in `template.config.ts → routes.breadcrumbResolver`.
 */
export type BreadcrumbResolver = (pathname: string, params?: BreadcrumbContext) => string[];

export interface BreadcrumbContext {
  /** Pre-resolved name for a dynamic route param (e.g., project name in /app/workspace/:id). */
  resolvedSegmentName?: string | null;
  /** The base app path; anything before this is dropped from the trail. Default: `/app`. */
  basePath?: string;
}

export function titleCase(input: string): string {
  if (!input) return '';
  return input
    .split(/[-_]/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

/** Default resolver: split the pathname after `basePath`, title-case each segment. */
export const defaultBreadcrumbResolver: BreadcrumbResolver = (pathname, context = {}) => {
  const basePath = context.basePath ?? '/app';
  const trimmed = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : pathname;
  const segments = trimmed.split('/').filter(Boolean);
  if (segments.length === 0) return ['Home'];
  return segments.map((segment, idx) => {
    // If this looks like a dynamic id (uuid, numeric, slug-like) and the caller
    // supplied a resolved name (e.g., the project name), prefer that.
    const isDynamicId = /^[0-9a-f-]{8,}$/i.test(segment) || /^\d+$/.test(segment);
    if (isDynamicId && context.resolvedSegmentName && idx === 1) {
      return context.resolvedSegmentName;
    }
    return titleCase(segment);
  });
};
